'use client';

import React from 'react';

import { useRouter } from 'next/navigation';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Button } from '@/modules/common/components/Button';
import Image from '@/modules/common/components/Image';
import { DataLayerFeature } from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { selectContentDataLayerEvent } from '@/modules/product/services/data-layer-events/select_content';

import { BlogPostsFieldsFragment } from '@/gql/__generated/graphql.types';

type BlogPostCardProps = {
  blog?: BlogPostsFieldsFragment | null;
};

export const BlogPostCard = ({ blog }: BlogPostCardProps) => {
  const router = useRouter();

  const categories = blog?.category;

  const handleOnClick = () => {
    selectContentDataLayerEvent({
      feature: DataLayerFeature.Blog,
      where: DataLayerWhere.BlogList,
      contentId: blog?.slug || undefined,
      contentType: 'Blog',
      name: blog?.title || '',
    });

    router.push(`/blog/post/${blog?.slug}`);
  };

  if (!blog) {
    return null;
  }

  return (
    <Button
      onClick={() => handleOnClick()}
      className="flex cursor-pointer flex-col justify-between whitespace-normal rounded-[20px] bg-gray-75 px-8 pt-12 text-center"
      variant="ghost"
      size="link"
    >
      <div className="flex flex-wrap items-center justify-center gap-2">
        {!!categories?.length &&
          categories.map((category) => (
            <div
              key={category}
              className="mx-auto mb-4 w-fit rounded-3xl bg-black bg-opacity-40 px-2 py-0.5 text-xs text-white"
            >
              {category}
            </div>
          ))}
      </div>
      <p className="text-2xl font-bold">{blog.title}</p>
      {blog.subtitle ?
        <p className="mt-2 text-gray-450">{blog.subtitle}</p>
      : null}

      <div className="pt-12">
        {blog.image && (
          <Image
            className="hidden w-full rounded-t-xl object-contain lg:block"
            src={blog.image?.url ?? ''}
            alt={blog.image.title ?? ''}
          />
        )}
        {blog.mobileImage && (
          <Image
            className="block w-full rounded-t-xl object-contain lg:hidden"
            src={blog.mobileImage?.url ?? ''}
            alt={blog.mobileImage.title ?? ''}
          />
        )}
      </div>
    </Button>
  );
};
